import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { PageProps } from 'types';

import { AppProvider } from 'contexts/appContext';
import { HistoryProvider } from 'hooks';
import { jobService } from 'services/jobService';
import { jobListingHash } from 'store/jobs/actions';
import HeaderLogoOnly from 'widgets/header/HeaderLogoOnly';
import HeaderWithProgressBar from 'widgets/header/HeaderWithProgressBar';
import HeaderWithSteps from 'widgets/header/headerWithSteps';

import '../../utils/nativeUtils';

import { useStore } from '../store';
import {
  checkLocalStorage,
  getCookie,
  loadServiceWorker,
  sendAnalytics,
} from '../utils';
import { GOOGLE_AD_SENSE_PUBLIC_KEY, HIDE_GOOGLE_ADS_ROUTES } from '../utils/constant';
import Header from '../widgets/header';

import 'react-toastify/dist/ReactToastify.css';
import '../css/application.css';
import '../less/less.css';
import '../components/index.css';
import '../layouts/index.css';
import '../widgets/index.css';
import 'styles/app.global.scss';

const Footer = dynamic(() => import('../widgets/Footer'));

const paidFixedPosCookie = '_zippia-paidFixedPos';

declare global {
  interface Window {
    userAgent: string;
    pageCategory: string;
    userId: string;
    isLoggedIn: boolean;
    userGroup: string;
    GA_INITIALIZED: boolean;
    btoa: (value: string) => string;
  }
}

if (!global.window) {
  global.window = {
    navigator: {
      userAgent: 'node',
    },
    userAgent: 'node',
    location: {
      protocol: 'http:',
      hostname: 'localhost',
      port: '8000',
    },
  } as Window & typeof globalThis;
}

if (typeof document === 'undefined') {
  global.document = {
    querySelector: () => null,
  } as unknown as Document;
}

window.btoa = (str) => Buffer.from(str).toString('base64');

export function reportWebVitals(metric: NextWebVitalsMetric) {
  switch (metric.name) {
    case 'FCP':
      sendAnalytics(metric);
      break;
    case 'LCP':
      sendAnalytics(metric);
      break;
    case 'CLS':
      sendAnalytics(metric);
      break;
    case 'FID':
      sendAnalytics(metric);
      break;
    case 'TTFB':
      sendAnalytics(metric);
      break;
    case 'Next.js-hydration':
      sendAnalytics(metric);
      break;
    case 'Next.js-route-change-to-render':
      sendAnalytics(metric);
      break;
    case 'Next.js-render':
      sendAnalytics(metric);
      break;
    default:
      break;
  }
}

export default function App({
  Component,
  pageProps,
}: AppProps<
  PageProps<{
    resumeData: Record<string, unknown>;
    stripeSubscriptionData: Record<string, unknown>;
    stickyHeader?: boolean,
    isHomePage?: boolean
  }>
>) {
  const router = useRouter();
  const store = useStore(pageProps, router);
  const [state, setState] = useState({
    showNewJobSearchUI: true,
    enablePaidFixedPos: false,
    showMiniLeftPanelUI: false,
  });

  checkLocalStorage();

  useEffect(() => {
    async function fetchListingHashes() {
      try {
        const listingHashes = await jobService.getUserlistinghashes();
        store.dispatch(jobListingHash(listingHashes));
      } catch (err) {
        console.error('Error occurred while fetching listing hashes');
      }
    }

    function setCookiesUsingUserGroups() {
      const userGroupPaidFixedPos = getCookie(paidFixedPosCookie);

      let enablePaidFixedPos = false;

      if (userGroupPaidFixedPos === 'groupB') {
        enablePaidFixedPos = true;
      }

      setState((state) => ({
        ...state,
        enablePaidFixedPos,
      }));
    }

    fetchListingHashes();

    setCookiesUsingUserGroups();

    window.addEventListener('load', loadServiceWorker);
    return () => {
      window.removeEventListener('load', loadServiceWorker);
    };
  }, [store]);

  const hideGoogleAd = HIDE_GOOGLE_ADS_ROUTES.some((route) => router?.asPath?.startsWith(route));

  const hideFooter = pageProps?.data?.notShowFooter || pageProps?.data?.notShowHeaderAndFooter;

  const getHeaderComponent = () => {
    if (pageProps?.data?.showHeaderOnlyLogo) {
      return <HeaderLogoOnly />;
    }

    if (pageProps?.data?.showHeaderWithSteps) {
      return (
        <HeaderWithSteps
          locals={pageProps.locals}
          criteria={pageProps.data.criteria}
          metadata={pageProps.data.metadata}
          steps={pageProps.data.headerSteps?.steps || []}
          currentStep={pageProps.data.headerSteps?.currentStep || ''}
        />
      );
    }

    if (pageProps?.data?.showHeaderWithProgressBar) {
      return <HeaderWithProgressBar stickyHeader={pageProps?.data?.stickyHeader} criteria={pageProps.locals.criteria} />;
    }

    if (pageProps?.data?.notShowHeaderAndFooter) {
      return null;
    }

    return <Header {...pageProps.data} />;
  };

  return (
    <HistoryProvider>
      <Provider store={store}>
        <Head>
          <meta
            name="viewport"
            content="height=device-height, width=device-width, initial-scale=1.0, viewport-fit=cover"
          />
        </Head>

        {!hideGoogleAd && (
          <Script
            async
            id="Adsense-id"
            crossOrigin="anonymous"
            strategy="afterInteractive"
            onError={(e) => {
              console.error('Script failed to load', e);
            }}
            src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${GOOGLE_AD_SENSE_PUBLIC_KEY}`}
          />
        )}

        {getHeaderComponent()}

        <AppProvider value={state}>
          <Component {...pageProps} />
          <ToastContainer
            position="bottom-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick={false}
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
            closeButton={false}
          />
        </AppProvider>

        {pageProps.data && !hideFooter && (
          <Footer
            showSalesContactInfo={pageProps.data?.showSalesContactInfo}
            hideFooterAds={pageProps.data?.hideFooterAds}
            isHomePage={pageProps.data?.isHomePage}
          />
        )}
      </Provider>
    </HistoryProvider>
  );
}
